import type {
  PlatformEntry,
  UsernameContactAddress,
  UsernameContactPhone,
  UsernameEducation,
  UsernameJob,
  UsernamePotentialOwner,
  UsernameReportData,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function normalizeCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
}

export function mapUsernameReportPayload(raw: unknown): UsernameReportData {
  const r = asRecord(raw) ?? {};

  const contactDetails =
    asRecord(r.contactDetails) ?? asRecord(r.contact_details) ?? {};
  const jobsEducation =
    asRecord(r.jobsEducation) ?? asRecord(r.jobs_education) ?? {};

  const platforms: PlatformEntry[] = asArray(r.platforms)
    .map((p) => {
      const item = asRecord(p);
      if (!item) {
        return null;
      }
      const entry: PlatformEntry = {
        name: toString(item.name),
        status: toString(item.status),
      };
      if (item.url) {
        entry.url = toString(item.url);
      }
      return entry;
    })
    .filter((p): p is PlatformEntry => p !== null && Boolean(p.name));

  const potentialOwnerRaw = asRecord(r.potentialOwner ?? r.potential_owner);
  const potentialOwnerBvId = toString(
    potentialOwnerRaw?.bvId ?? potentialOwnerRaw?.bv_id
  );
  const potentialOwnerName = toString(potentialOwnerRaw?.name);
  const potentialOwner: UsernamePotentialOwner | null =
    potentialOwnerRaw && potentialOwnerName
      ? {
          name: potentialOwnerName,
          ...(potentialOwnerBvId ? { bvId: potentialOwnerBvId } : {}),
          primaryAddress: toString(
            potentialOwnerRaw.primary_address ??
              potentialOwnerRaw.primaryAddress
          ),
          primaryEmail: toString(
            potentialOwnerRaw.primary_email ?? potentialOwnerRaw.primaryEmail
          ),
          primaryPhone: toString(
            potentialOwnerRaw.primary_phone ?? potentialOwnerRaw.primaryPhone
          ),
        }
      : null;

  const otherPotentialOwners: UsernamePotentialOwner[] = asArray(
    r.otherPotentialOwners ?? r.other_potential_owners
  )
    .map((item) => {
      const name =
        typeof item === 'string'
          ? item
          : toString(asRecord(item)?.name ?? asRecord(item)?.username);
      if (!name) {
        return null;
      }
      const record = asRecord(item);
      return {
        name,
        bvId: record?.bvId ? toString(record.bvId) : undefined,
        primaryAddress: toString(
          record?.primaryAddress ?? record?.primary_address
        ),
        primaryEmail: toString(record?.primaryEmail ?? record?.primary_email),
        primaryPhone: toString(record?.primaryPhone ?? record?.primary_phone),
      } as UsernamePotentialOwner;
    })
    .filter((o): o is UsernamePotentialOwner => o !== null);

  const potentialAddresses: UsernameContactAddress[] = asArray(
    contactDetails.potential_addresses ?? contactDetails.potentialAddresses
  )
    .map((a) => {
      const item = asRecord(a);
      return {
        full: toString(item?.full ?? item?.address),
        type: toString(item?.type),
      };
    })
    .filter((a) => Boolean(a.full));

  const potentialPhoneNumbers: UsernameContactPhone[] = asArray(
    contactDetails.potential_phone_numbers ??
      contactDetails.potentialPhoneNumbers
  )
    .map((p) => {
      const item = asRecord(p);
      return {
        number: toString(item?.number),
        type: toString(item?.type),
      };
    })
    .filter((p) => Boolean(p.number));

  const jobs: UsernameJob[] = asArray(jobsEducation.jobs)
    .map((j) => {
      const item = asRecord(j);
      if (!item) {
        return null;
      }
      return {
        company: toString(item.company),
        period: toString(item.period),
        titles: asArray(item.titles).map(toString).filter(Boolean),
      };
    })
    .filter((j): j is UsernameJob => j !== null && Boolean(j.company));

  const education: UsernameEducation[] = asArray(jobsEducation.education)
    .map((e) => {
      const item = asRecord(e);
      if (!item) {
        return null;
      }
      return {
        school: toString(item.school ?? item.name),
        attended: toString(item.attended),
        degrees: asArray(item.degrees).map(toString).filter(Boolean),
      };
    })
    .filter((e): e is UsernameEducation => e !== null && Boolean(e.school));

  return {
    username: toString(r.username),
    platforms,
    ownerName: toString(r.ownerName ?? r.owner_name),
    ownersCount: normalizeCount(r.ownersCount ?? r.owners_count),
    primaryAddress: toString(r.primaryAddress ?? r.primary_address),
    primaryPhone: toString(r.primaryPhone ?? r.primary_phone),
    addressCount: normalizeCount(r.addressCount ?? r.address_count),
    emailCount: normalizeCount(r.emailCount ?? r.email_count),
    phoneCount: normalizeCount(r.phoneCount ?? r.phone_count),
    hasEducationRecords: toString(
      r.hasEducationRecords ?? r.has_education_records
    ),
    hasJobRecords: toString(r.hasJobRecords ?? r.has_job_records),
    hasMultipleOwners: toString(r.hasMultipleOwners ?? r.has_multiple_owners),
    potentialOwner,
    otherPotentialOwners,
    potentialAddresses,
    potentialPhoneNumbers,
    jobs,
    education,
    permalink: r.permalink ? toString(r.permalink) : undefined,
  };
}
